// ATALHOS INSTITUCIONAIS EM ÍCONE (rodapé) — pedido do cs2619 (26/08/2026).
// Trabalhe conosco, Política de privacidade e Quem somos deixam o menu e ficam aqui como
// ÍCONE; clicar abre o CONTEÚDO da própria página num modal, sem sair da tela.
//
// O conteúdo é o HTML montado no editor de páginas do CRM. Ele NÃO vem no payload da home
// (`optimizeHomePayloadForClient` zera `menus[].Conteudo` para a home não carregar dezenas
// de KB que quase ninguém abre), então é buscado SOB DEMANDA no primeiro clique, via a
// query `menus` no BFF — e guardado em memória para as próximas aberturas.
// Esse HTML traz o próprio <style> ESCOPADO em `.itp` e um wrapper <div class="itp">, então
// injetar aqui não vaza CSS pro resto do site (conferido no cs2619: 8-12 KB por página,
// todas as regras no formato `.itp …`). Se a busca falhar ou a página estiver vazia, o
// modal não trava: leva o visitante para a rota `/menu/<Titulo>`, que sempre funcionou.
import Link from 'next/link';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaBriefcase, FaInfoCircle, FaShieldAlt, FaTimes } from 'react-icons/fa';
import { useRouter } from 'next/router';
import { menusClient } from '../../../services/menus';
import { removerAcentos } from '../../../utils/FunctionsGlobals';
import { chaveDoAtalho, type FooterShortcutKey } from '../../../utils/footerShortcuts';
import { trackMetaEvent } from '../../../utils/metaPixel';
import styles from './FooterShortcuts.module.css';

const ICONES: Record<FooterShortcutKey, React.ComponentType<any>> = {
    trabalhe: FaBriefcase,
    privacidade: FaShieldAlt,
    'quem-somos': FaInfoCircle,
};

export interface FooterShortcutItem {
    titulo: string;
    conteudo?: string | null;
}

interface Props {
    /** menus do site que já foram filtrados como atalhos (ver ehAtalhoDeRodape) */
    itens: FooterShortcutItem[];
    site: any;
    /** cor legível sobre o fundo onde os ícones vivem */
    cor: string;
    /** href já com ?codsite quando for o caso (fallback) */
    hrefDe: (_titulo: string) => string;
    origin?: any;
    codsite?: any;
}

const mesmoTitulo = (a: unknown, b: unknown) =>
    removerAcentos(String(a ?? '')).toLowerCase().trim() === removerAcentos(String(b ?? '')).toLowerCase().trim();

const FooterShortcuts: React.FC<Props> = ({ itens, site, cor, hrefDe, origin, codsite }) => {
    const router = useRouter();
    const [aberto, setAberto] = useState<FooterShortcutItem | null>(null);
    const [conteudo, setConteudo] = useState<string>('');
    const [carregando, setCarregando] = useState(false);
    const fecharRef = useRef<HTMLButtonElement | null>(null);
    const abridorRef = useRef<HTMLElement | null>(null);
    // cache em memória: a resposta traz TODOS os menus, então uma busca serve para os três
    // ícones — sem isto, abrir os três seguidos dispararia três requisições iguais.
    const cacheRef = useRef<Map<string, string>>(new Map());
    const listaRef = useRef<any[] | null>(null);
    const mountedRef = useRef(true);
    // Antes de hidratar, o ícone é um <a> pra /menu/<Titulo>: quem está sem JS (ou com o
    // chunk ainda carregando) continua alcançando a informação.
    const [podeAbrirModal, setPodeAbrirModal] = useState(false);

    useEffect(() => {
        mountedRef.current = true;
        setPodeAbrirModal(true);
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const fechar = useCallback(() => {
        setAberto(null);
        setConteudo('');
        setCarregando(false);
        // devolve o foco a quem abriu (leitor de tela não fica perdido no fim da página)
        abridorRef.current?.focus?.();
        abridorRef.current = null;
    }, []);

    useEffect(() => {
        if (!aberto) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') fechar();
        };
        document.addEventListener('keydown', onKey);
        // trava o scroll do fundo enquanto o modal está aberto
        const overflowAnterior = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        fecharRef.current?.focus?.();
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = overflowAnterior;
        };
    }, [aberto, fechar]);

    /**
     * Abre o modal e garante o conteúdo: usa o cache, senão busca a query `menus` e casa
     * pelo título. Sem conteúdo (página vazia ou busca falhou), navega para /menu/<Titulo>
     * — o visitante vê a informação de um jeito ou de outro.
     */
    const abrir = useCallback(
        async (item: FooterShortcutItem, alvo: HTMLElement) => {
            abridorRef.current = alvo;
            trackMetaEvent(site, 'ViewContent', {
                content_name: item.titulo,
                content_category: 'footer_institucional',
            });

            const doPayload = String(item.conteudo || '').trim();
            const emCache = cacheRef.current.get(item.titulo) || '';
            const jaTenho = doPayload || emCache;
            setAberto(item);
            if (jaTenho) {
                setConteudo(jaTenho);
                return;
            }

            setConteudo('');
            setCarregando(true);
            const lista = listaRef.current ?? (await menusClient(origin, codsite));
            if (!mountedRef.current) return;
            if (lista) {
                listaRef.current = lista;
                // guarda o conteúdo de todos de uma vez (os outros ícones abrem instantâneos)
                lista.forEach((m: any) => {
                    const html = String(m?.Conteudo || '').trim();
                    if (m?.titulo && html) cacheRef.current.set(String(m.titulo), html);
                });
            }
            const achado = (lista || []).find(
                (m: any) => mesmoTitulo(m?.titulo, item.titulo) && String(m?.Conteudo || '').trim(),
            );
            const html = String(achado?.Conteudo || cacheRef.current.get(item.titulo) || '').trim();
            setCarregando(false);
            if (html) {
                cacheRef.current.set(item.titulo, html);
                setConteudo(html);
                return;
            }
            // nada pra mostrar aqui: a rota da página é o caminho garantido
            setAberto(null);
            router.push(hrefDe(item.titulo)).catch(() => undefined);
        },
        [codsite, hrefDe, origin, router, site],
    );

    if (!itens.length) return null;

    return (
        <>
            <ul className={styles.lista} aria-label="Informações institucionais">
                {itens.map((item) => {
                    const key = chaveDoAtalho(item.titulo);
                    const Icone = (key && ICONES[key]) || FaInfoCircle;
                    const comum = {
                        className: styles.botao,
                        style: { color: cor, borderColor: cor },
                        title: item.titulo,
                        'aria-label': item.titulo,
                    };
                    return (
                        <li key={item.titulo}>
                            {podeAbrirModal ? (
                                <button
                                    type="button"
                                    {...comum}
                                    onClick={(e) => {
                                        void abrir(item, e.currentTarget);
                                    }}
                                >
                                    <Icone aria-hidden="true" />
                                </button>
                            ) : (
                                // sem JS hidratado ainda: o ícone é um link normal pra página
                                <Link prefetch={false} href={hrefDe(item.titulo)} {...comum}>
                                    <Icone aria-hidden="true" />
                                </Link>
                            )}
                        </li>
                    );
                })}
            </ul>

            {aberto ? (
                <div
                    className={styles.overlay}
                    role="dialog"
                    aria-modal="true"
                    aria-label={aberto.titulo}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) fechar();
                    }}
                >
                    <div className={styles.modal}>
                        <div className={styles.modalHead}>
                            <h2 className={styles.modalTitulo}>{aberto.titulo}</h2>
                            <button
                                ref={fecharRef}
                                type="button"
                                className={styles.fechar}
                                onClick={fechar}
                                aria-label="Fechar"
                                title="Fechar"
                            >
                                <FaTimes aria-hidden="true" />
                            </button>
                        </div>
                        {carregando ? (
                            <div className={styles.carregando} role="status">
                                <span className={styles.spinner} aria-hidden="true" />
                                Carregando…
                            </div>
                        ) : (
                            <div
                                className={styles.modalCorpo}
                                // HTML do editor de páginas do próprio CRM (mesma fonte que a
                                // rota /menu/<Titulo> renderiza), com CSS escopado em `.itp`.
                                dangerouslySetInnerHTML={{ __html: conteudo }}
                            />
                        )}
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default FooterShortcuts;
